import { approveLeave, rejectLeave, processCancellationApproval, approveShortLeave, rejectShortLeave, approveEncashment, rejectEncashment } from '../../api/Attendance/leaves';
import { approveShiftSwap, rejectShiftSwap, acceptShiftSwap, declineShiftSwap } from '../../api/Attendance/shiftSwap';
import { decideCorrectionRequest } from '../../api/Attendance/correctionRequests';
import { decideOutOfBoundsApproval, type OutOfBoundsClockType } from '../../api/Attendance/outOfBoundsApproval';
import { overtimeApproval } from '../../api/Attendance/attendance';
import { decideShiftPay } from '../../api/Attendance/shiftPayApprovals';
import { editUserClaim } from '../../api/Payroll/claim';
import { UserClaimStatus } from '../../models/payroll';
import { MatchStatus, resolveEnum } from '../../models/tournament';
import { reviewAssetRequest, AssetRequestStatus } from '../../api/General/assets';
import { acceptTerms } from '../../api/General/termsAndPolicies';
import { acceptTerms as acceptPlatformTerms, getCurrentTerms } from '../../api/General/PlatformTerms';
import { acceptAssignment, declineAssignment } from '../../api/TournamentManagement/refereeApi';
import { managerApproveMovement, hrApproveMovement, rejectMovement as rejectInternalMovement } from '../../api/Employee/internalMovements';
import { approveMovement as approveEmployeeMovement, rejectMovement as rejectEmployeeMovement } from '../../api/Employee/employeeMovement';
import { approveProfile, rejectProfile } from '../../api/PublicProfile/publicProfileApi';
import { acknowledgeSnapshot } from '../../api/PreformanceManagement/PerformanceSnapshot';
import { getLockedModule } from '../../data/lockedRoutes';
import type { PendingActionItem, PendingActionSource } from '../../api/pendingActions';
import { LeaveRequestStatus } from '../../models/attendance';

// Per-source action dispatch for the Pending Actions inbox. The backend enforces
// authorization; this layer only maps (item, decision) → the domain endpoint.
// `positive` = Approve / Accept / Complete; `!positive` = Reject / Decline.

export interface DispatchResult {
  errors: { code?: string; message?: string; description?: string }[];
  /**
   * True when an approval was accepted but did not finish the request: a multi-step leave workflow
   * moved to its next step and the leave is still Pending (LeaveService returns Status=Pending for
   * that case). The row stays listed, greyed (e1539e9c6), so the toast must not say it was removed.
   */
  awaitingNextApprover?: boolean;
}

/**
 * A negative Decision2/Accept2 always needs a reason wherever the endpoint can
 * carry one. Rule, not allow-list: an allow-list silently omits every source
 * added later, which is how employees ended up being rejected with no
 * explanation from short leave, encashment, shift swap and referee.
 */
export function requiresComment(item: PendingActionItem, positive: boolean): boolean {
  if (positive) return false;
  if (!supportsNote(item)) return false;
  return item.actionStyle === 'Decision2' || item.actionStyle === 'Accept2';
}

/**
 * Whether the confirmation step can carry a note. Shift-swap Accept/Decline
 * endpoints take no body, and terms acceptance is a bare POST — showing a
 * note field there would silently drop the user's text.
 */
export function supportsNote(item: PendingActionItem): boolean {
  return (
    item.source !== 'ShiftSwapAcceptance' &&
    item.source !== 'Terms' &&
    item.source !== 'PlatformTerms' &&
    // CO-583 — OvertimeShiftApprovalInput is { shiftId, isApproved, overridePreviousDecision } and
    // NOTHING else (the DTO drops undeclared body fields silently), so a note box here would ask the
    // reviewer to justify a wage refusal and then throw the justification away. Same rule, same
    // reason, as the two note-less endpoints above.
    item.source !== 'OvertimeApproval'
  );
}

/**
 * Sources whose single positive action fires without a confirmation step: a
 * bare POST that carries no body and has no consequence to warn about. Anything
 * else routes through DecisionDialog so the user can read (and annotate) first.
 */
export function isOneTap(item: PendingActionItem, positive: boolean): boolean {
  if (!positive || supportsNote(item) || requiresComment(item, positive)) return false;
  // PlatformTerms is deliberately NOT one-tap: accepting it needs an extra hop to
  // resolve the terms id for the kind (see the PlatformTerms case below), and the
  // row is Blocking — a silent one-tap gave the user no place to see it fail.
  return item.source === 'Terms';
}

/**
 * Whether the NEGATIVE half of a two-button row is honest for THIS item.
 *
 * CO-780. Accept and Decline stopped agreeing on the referee row: the owner's 2026-09-21 decision
 * widened `AcceptAssignment` to admit `Completed` and `UnderRevision` (100 stranded Pending rows on
 * prod co 29 sat on auto-completed matches), while `DeclineAssignment` was deliberately NOT widened —
 * it is one of the two paths that archive the assignment and void its pay, so letting somebody
 * decline after officiating would let them unpay themselves. Since `GetMyPendingAssignments` has no
 * match-status filter, those same rows are in this inbox, where a live Decline can only ever 400.
 * `views/TournamentManagement/my-assignments` already hides its Decline for exactly this reason;
 * this is the same gate on the same condition, so the two surfaces cannot drift apart.
 *
 * 🔴 FAIL-OPEN, on purpose. It withholds the button ONLY when the server told us a status AND that
 * status is not `Scheduled`. No meta, an unparseable value, or an older server that does not emit
 * `matchStatus` yet all KEEP the button — hiding a legitimate decline (stranding an official who
 * genuinely cannot make a fixture) is a worse failure than showing one that gets refused with a
 * now-translated reason.
 *
 * ⚠️ This mirrors only the server's FIRST decline refusal (`IsArchived || Status != Scheduled`;
 * archived rows never reach this inbox). The second one is kickoff-based and needs the company time
 * zone, which the inbox row does not carry — a Scheduled match whose kickoff has passed still shows
 * Decline and is refused with `ASSIGNMENT_DECLINE_MATCH_STARTED`, which now renders translated.
 */
export function offersNegativeDecision(item: PendingActionItem): boolean {
  if (item.source !== 'Referee') return true;
  const raw = item.meta?.matchStatus;
  if (!raw) return true;
  const status = resolveEnum(MatchStatus, raw);
  if (status === undefined) return true;
  return status === MatchStatus.Scheduled;
}

/** The single-action label for a Complete1 row, per source. */
export function completeActionLabel(source: PendingActionSource): string {
  return source === 'PerformanceSnapshot' ? 'Acknowledge' : 'Accept';
}

export interface RouteAccessContext {
  role: string;
  isManager: boolean;
  /**
   * Feature/country gates that guard a route in App.tsx beyond its role check.
   * Tri-state on purpose: `undefined` means the caller does not know, and an
   * unknown gate keeps today's link rather than hiding a page that works. Pass
   * them wherever they are available so the gate can actually withhold.
   */
  shiftSwapEnabled?: boolean;
  complianceCountry?: string | null;
  /**
   * True when this tenant is Free-tier (and the caller is not an Operator previewing).
   * Free-tier does NOT bounce — MasterLayout wraps the page in LockedShell, a
   * non-interactive brochure preview. So a deep link to a locked module lands the
   * user on a page that renders and cannot act, which is worse than the in-inbox
   * dialog (where the decision endpoints still exist). Same tri-state rule as above:
   * `undefined` = caller doesn't know = keep the link.
   */
  isFreeTier?: boolean;
}

/** `false` only when the context says the gate is closed; unknown keeps the link. */
function shiftSwapGate(ctx: RouteAccessContext): boolean {
  return ctx.shiftSwapEnabled !== false;
}

/** /payroll/compliance-center is only mounted for UAE/KSA companies (App.tsx). */
function complianceCenterGate(ctx: RouteAccessContext): boolean {
  if (ctx.complianceCountry === undefined || ctx.complianceCountry === null) return true;
  return ctx.complianceCountry === 'UAE' || ctx.complianceCountry === 'KSA';
}

/**
 * Web deep-link per source. `allowedRoles` mirrors the App.tsx route guard for
 * that path (RoleProtectedRoute semantics: primary role + synthetic 'Manager'
 * when the user has direct reports) — a link the guard would bounce to '/' is
 * worse than no link. `gate` mirrors the same for the non-role guards: a route
 * wrapped in a feature guard (ShiftSwapEnabledRoute) or rendered only under a
 * country conditional is not reachable for everyone, and without this a deep
 * link lands the user on the '/' fallback with no explanation.
 * Sources without an entry have no richer page (detail dialog only).
 */
const SOURCE_ROUTE_DEFS: Partial<
  Record<PendingActionSource, { path: string; allowedRoles?: string[]; gate?: (ctx: RouteAccessContext) => boolean }>
> = {
  Leave: { path: '/leave-approvals', allowedRoles: ['Manager', 'Hr', 'Chief'] },
  // ShiftSwapEnabledRoute (App.tsx) redirects to '/' when the feature is off for
  // the user or the company.
  ShiftSwap: { path: '/shift-swaps', gate: shiftSwapGate },
  ShiftSwapAcceptance: { path: '/shift-swaps', gate: shiftSwapGate },
  Claim: { path: '/payroll/claims', allowedRoles: ['Hr'] },
  AttendanceCorrection: { path: '/attendance/correction-requests' },
  Onboarding: { path: '/onboarding/complete' },
  Terms: { path: '/terms-and-policies/accept' },
  PlatformTerms: { path: '/terms-and-policies/accept' },
  Survey: { path: '/employee-impact/enps' },
  ReturnToWork: { path: '/return-to-work' },
  // Previously pageless even though a real page exists.
  OutOfBoundsClock: { path: '/attendance/reports/out-of-bounds' },
  // CO-583 — the dedicated queue, and the SAME path AttendanceService stamps into the type-10
  // notification's LinkId. NO allowedRoles, mirroring the App.tsx route, which no longer carries a
  // RoleProtectedRoute: the page has a self mode, so an employee following their own overtime item
  // lands on their own rows rather than being bounced to '/' with no explanation. The server still
  // decides what those rows are and whether they carry a decision.
  OvertimeApproval: { path: '/attendance/overtime-approvals' },
  // Hourly pay §9 — the queue, mirroring the App.tsx guard (SHIFT_APPROVER_ROLES). The server stamps
  // `Meta.deepLink` = `/attendance/shift-approvals?userId&from&to`, which getSourceRoute prefers.
  ShiftPayApproval: { path: '/attendance/shift-approvals', allowedRoles: ['Manager', 'Hr'] },
  AssetRequest: { path: '/core-hr/assets-management', allowedRoles: ['Hr'] },
  Referee: { path: '/tournament-management/my-assignments' },
  // Approval-wave sources.
  // InternalMovement has TWO approval stages and the manager one (notification type 80) is addressed to
  // the employee's direct manager — an 'Hr'-only entry withheld the link from exactly the recipient it
  // was minted for, and the route itself bounced them. Mirrors the App.tsx MANAGER_ROLES guard; the tab
  // is pinned because the lifecycle page defaults to Onboarding.
  InternalMovement: { path: '/employment-lifecycle?tab=internal-movements', allowedRoles: ['Manager', 'Hr', 'Chief'] },
  // EmployeeMovement stays Hr-only on purpose: EmployeeMovementController's Approve/Reject is an HR-only
  // flow with no manager stage.
  EmployeeMovement: { path: '/employment-lifecycle', allowedRoles: ['Hr'] },
  PublicProfile: { path: '/hr-admin-public-profile-settings', allowedRoles: ['Hr', 'Admin'] },
  GpssaPending: { path: '/payroll/compliance-center', allowedRoles: ['Hr'], gate: complianceCenterGate },
  GoalBlocker: { path: '/employee-impact/goals' },
  Reflection: { path: '/employee-impact/reflections' },
  ReflectionResponse: { path: '/employee-impact/team-reflections', allowedRoles: ['Manager', 'Hr', 'Chief'] },
  // Un-hidden by owner decision (product reversal of f5d4cef7 / 53ea5c98): the routes and
  // the 'Leaves' nav entries are live again, so the deep links are added here in the same
  // change. Both routes are deliberately unguarded in App.tsx (self-service pages whose
  // approver tabs are conditional inside the component), hence no `allowedRoles` — the
  // requester following their own item and the approver following a queued one both land
  // on a page that renders for them.
  ShortLeave: { path: '/short-leave' },
  Encashment: { path: '/encashment' },
  // The item asks the employee to PICK their restricted-holiday days, and
  // views/Attendance/RestrictedHolidays already renders exactly that (Select /
  // Deselect against Leave/RestrictedHoliday/{id}/Select|Deselect, which carry no
  // role policy). It was only ever mounted embedded inside the HR-only Leave
  // Settings tab; /restricted-holidays now mounts the same component standalone and
  // unguarded, so no allowedRoles — the recipient is any employee.
  RestrictedHoliday: { path: '/restricted-holidays' },
  // Offboarding is Hr-ONLY, and stricter than the /employment-lifecycle route guard
  // (MANAGER_ROLES) on purpose: every OffboardingProcess endpoint — including the
  // UpdateTaskStatus call that actually completes one of these tasks — is behind the
  // "HrOnly" policy (Admin excluded, see CLAUDE.md Role Model). A Manager/Chief
  // following the link would land on a tab whose data calls all 403, so they keep the
  // in-inbox detail dialog instead. Tab pinned because the page defaults to Onboarding.
  Offboarding: { path: '/employment-lifecycle?tab=offboarding', allowedRoles: ['Hr'] },
  // No entry (deliberate — the in-inbox dialog is the honest fallback):
  //   PerformanceSnapshot — /performance-snapshot is the HR authoring view, not
  //     the employee's acknowledgement surface.
  // KNOWN GAP: a non-HR assignee (the IT/Finance owner of an exit task, or the leaver
  // themselves) can hold an Offboarding item with no way to complete it — there is no
  // authenticated "my offboarding tasks" endpoint the way onboarding has
  // OnboardingProcess/MyDashboardTasks. That needs a server endpoint, not a route.
};

/**
 * The deep-link for an item, or undefined when none exists or the caller's role/gates
 * can't open it.
 *
 * Takes the ITEM, not just the source, because the server can stamp a row-specific
 * deep link into `Meta.deepLink` — CO-583's overtime rows carry
 * `/attendance/overtime-approvals?userId=&shiftId=&from=&to=`, which lands the reviewer
 * on the one shift instead of an unfiltered queue. The static `def.path` is still what
 * every gate is evaluated against (role, feature, country, Free-tier soft-lock), so a
 * server-supplied link can never widen access: `Meta.deepLink` only refines the
 * destination of a route the caller was already allowed to open. Sources whose meta
 * carries no deepLink (the other 13) fall through to `def.path` unchanged.
 */
export function getSourceRoute(item: PendingActionItem, ctx: RouteAccessContext): string | undefined {
  const def = SOURCE_ROUTE_DEFS[item.source];
  if (!def) return undefined;
  if (def.gate && !def.gate(ctx)) return undefined;
  // Subscription gate, applied to EVERY source rather than per-entry: the module
  // soft-lock table is the single source of truth for what a Free tenant may open
  // (data/lockedRoutes.ts, kept in sync with the backend TierEnforcementMiddleware).
  // Strip the query string first — LOCKED_ROUTES matches on pathname only, and
  // '/employment-lifecycle?tab=offboarding' must not miss a prefix match.
  if (ctx.isFreeTier === true && getLockedModule(def.path.split('?')[0]) !== null) return undefined;
  const target = item.meta?.deepLink ?? def.path;
  if (!def.allowedRoles) return target;
  const effectiveRoles = new Set([ctx.role]);
  if (ctx.isManager) effectiveRoles.add('Manager');
  return def.allowedRoles.some((r) => effectiveRoles.has(r)) ? target : undefined;
}

/**
 * Whether a source has a decision endpoint behind it at all.
 *
 * `false` mirrors the OpenOnly block at the bottom of `dispatchDecision`, which
 * returns `{ code: 'NotDispatchable' }` — a bare code with no `description` or
 * `message`, so `usePendingActionDecision` renders it as the generic
 * "Something went wrong". Today nothing reaches it, because the server pairs
 * every one of these sources with `ActionStyle = OpenOnly`
 * (`Services/Dashboard/PendingActionsService.cs`) and the buttons key off
 * `actionStyle`. That is a convention across a wire boundary, not a guarantee:
 * one server-side style change would paint Approve/Reject that can only fail.
 * Every quick-action surface gates on this so the dead button is impossible.
 *
 * TOTAL Record on purpose — a new PendingActionSource must be classified here
 * (build error) rather than defaulting to "dispatchable" and reintroducing the
 * exact failure this guards.
 */
const DISPATCHABLE_BY_SOURCE: Record<PendingActionSource, boolean> = {
  Leave: true,
  ShiftSwap: true,
  ShiftSwapAcceptance: true,
  Claim: true,
  AttendanceCorrection: true,
  OutOfBoundsClock: true,
  ShortLeave: true,
  Encashment: true,
  AssetRequest: true,
  Terms: true,
  PlatformTerms: true,
  Referee: true,
  InternalMovement: true,
  EmployeeMovement: true,
  PublicProfile: true,
  PerformanceSnapshot: true,
  OvertimeApproval: true,
  ShiftPayApproval: true,
  // Resolved on the source's own page — these render "Open", never a decision.
  Onboarding: false,
  Offboarding: false,
  Survey: false,
  ReturnToWork: false,
  Reflection: false,
  ReflectionResponse: false,
  GoalBlocker: false,
  GpssaPending: false,
  RestrictedHoliday: false,
};

/** True when `dispatchDecision` can actually carry a decision for this source. */
export function isDispatchable(source: PendingActionSource): boolean {
  return DISPATCHABLE_BY_SOURCE[source] === true;
}

export async function dispatchDecision(
  item: PendingActionItem,
  positive: boolean,
  note: string,
): Promise<DispatchResult> {
  const numericId = Number.parseInt(item.id, 10);
  switch (item.source) {
    case 'Leave': {
      // Cancellation requests approve/reject through their own endpoint.
      if (item.meta?.action === 'cancellation') {
        return processCancellationApproval(numericId, positive, note || undefined);
      }
      if (!positive) return rejectLeave(numericId, note);
      const approved = await approveLeave(numericId, note || undefined);
      return {
        ...approved,
        awaitingNextApprover: approved.errors.length === 0 && approved.payload?.status === LeaveRequestStatus.Pending,
      };
    }
    case 'ShiftSwap':
      return positive
        ? approveShiftSwap(numericId, { comments: note || null })
        : rejectShiftSwap(numericId, { comments: note || null });
    case 'ShiftSwapAcceptance':
      return positive ? acceptShiftSwap(numericId) : declineShiftSwap(numericId);
    case 'Claim':
      return editUserClaim(numericId, {
        id: numericId,
        status: positive ? UserClaimStatus.Approved : UserClaimStatus.Rejected,
        managerComment: note || undefined,
      });
    case 'AttendanceCorrection':
      return decideCorrectionRequest({
        requestId: Number.parseInt(item.meta?.requestId ?? item.id, 10),
        isApproved: positive,
        comment: note || undefined,
      });
    case 'OutOfBoundsClock':
      return decideOutOfBoundsApproval({
        shiftId: Number.parseInt(item.meta?.shiftId ?? item.id, 10),
        clockType: (item.meta?.clockType as OutOfBoundsClockType) ?? 'ClockIn',
        isApproved: positive,
        comment: note || undefined,
      });
    case 'ShortLeave':
      return positive
        ? approveShortLeave(numericId, { comments: note || undefined })
        : rejectShortLeave(numericId, { comments: note || undefined });
    case 'Encashment':
      return positive
        ? approveEncashment(numericId, { comments: note || undefined })
        : rejectEncashment(numericId, { comments: note || undefined });
    case 'AssetRequest':
      return reviewAssetRequest({
        requestId: numericId,
        status: positive ? AssetRequestStatus.Approved : AssetRequestStatus.Rejected,
        comment: note || undefined,
      });
    case 'Terms':
      return acceptTerms();
    case 'PlatformTerms': {
      // The row is keyed by terms KIND; acceptance is keyed by the terms RECORD.
      // The aggregator emits neither: `Id` is the kind name ("GeneralUsage") and
      // Meta carries only `kind`, so parsing either as an id yields NaN and the
      // accept could never fire. Resolve the kind's current published version
      // first, exactly as mobile does. Meta.termsId is still honoured so the
      // round-trip disappears if the server ever starts emitting it.
      const metaTermsId = Number.parseInt(item.meta?.termsId ?? '', 10);
      if (Number.isFinite(metaTermsId)) return acceptPlatformTerms(metaTermsId);
      const current = await getCurrentTerms(item.meta?.kind ?? item.id);
      if (current.errors.length > 0) return { errors: current.errors };
      const termsId = current.payload?.id;
      if (!termsId) return { errors: [{ code: 'MissingTermsId' }] };
      return acceptPlatformTerms(termsId);
    }
    case 'Referee': {
      const matchId = Number.parseInt(item.meta?.matchId ?? item.id, 10);
      return positive
        ? acceptAssignment({ matchId, reason: note || undefined })
        : declineAssignment({ matchId, reason: note || undefined });
    }
    case 'InternalMovement': {
      // Two approval stages share one queue; Meta.stage says which endpoint owns this row.
      if (!positive) return rejectInternalMovement(numericId, { rejectionReason: note });
      return item.meta?.stage === 'hr' ? hrApproveMovement(numericId) : managerApproveMovement(numericId);
    }
    case 'EmployeeMovement':
      return positive ? approveEmployeeMovement(numericId) : rejectEmployeeMovement(numericId, note);
    case 'PublicProfile':
      return positive ? approveProfile(numericId) : rejectProfile(numericId, note || undefined);
    case 'PerformanceSnapshot':
      // Not a Result<> endpoint: it throws on failure, which the caller's catch reports.
      await acknowledgeSnapshot(numericId);
      return { errors: [] };
    case 'OvertimeApproval':
      // 🔴 The SAME endpoint the report tab and the approvals page post to — no second write path,
      // so the 403 / 409 / paid-period rules cannot drift. `numericId` is the SHIFT id (Meta carries
      // it too, for a client that would rather not parse the item id).
      return overtimeApproval({
        shiftId: Number.parseInt(item.meta?.shiftId ?? item.id, 10),
        isApproved: positive,
        // NEVER from the inbox. A reversal is an explicit act taken after the reviewer has been
        // shown the decision on record, and the inbox only ever lists PENDING shifts — sending the
        // override here would let a quick-action silently overwrite somebody else's decision.
      });
    case 'ShiftPayApproval':
      // Hourly pay §7.2 — the SAME `{id}/Decide` endpoint the queue posts to, so the 400 noteRequired
      // / 403 selfApproval / 409 alreadyDecided / 423 payPeriodLocked rules cannot drift. The item id
      // IS ShiftPayApproval.Id. NEVER `overridePreviousDecision` from the inbox: it lists Pending
      // items only, and a quick action must not overwrite a decision somebody else recorded.
      return decideShiftPay(Number.parseInt(item.meta?.shiftPayApprovalId ?? item.id, 10), {
        action: positive ? 'approve' : 'reject',
        ...(note ? { note } : {}),
      });
    // OpenOnly sources: the work happens on the source's own page, so these rows
    // render an "Open" affordance and never reach a decision dispatch. Listed
    // explicitly so the exhaustiveness check below stays meaningful.
    case 'Onboarding':
    case 'Offboarding':
    case 'Survey':
    case 'ReturnToWork':
    case 'Reflection':
    case 'ReflectionResponse':
    case 'GoalBlocker':
    case 'GpssaPending':
    case 'RestrictedHoliday':
      return { errors: [{ code: 'NotDispatchable' }] };
    default: {
      // Compile-time guard: a new PendingActionSource that nobody wired breaks the
      // build here instead of shipping a dead button.
      const unhandled: never = item.source;
      return { errors: [{ code: 'UnsupportedSource', description: String(unhandled) }] };
    }
  }
}
